import { isEmbeddedInIframe } from "@/shared/lib/deeplink";

export interface HandshakeMetadata {
  userAgent: string;
  locale: string;
  referrer: string;
  queryParams: Record<string, string>;
  timestamp: string;
  /** Loaded inside an iframe (e.g. a merchant embedding checkout in a page). */
  isEmbedded: boolean;
  /** Loaded inside a native in-app WebView (merchant or Tamara app), by UA. */
  isWebView: boolean;
}

/**
 * Best-effort native WebView detection from the user agent:
 * - Android System WebView adds `; wv)` to the UA.
 * - iOS WKWebView omits the `Safari/` token that Safari, Chrome and Firefox
 *   on iOS all send.
 * Apps that override their UA can defeat this, so the raw UA is still sent.
 */
export function isWebViewUserAgent(ua: string): boolean {
  if (/; wv\)/.test(ua)) return true;
  return /iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua);
}

/**
 * Collects the client-side metadata for a checkout handshake: query params,
 * user agent, locale (locale query wins over navigator.language), referrer,
 * and whether the page is embedded in an iframe / native WebView.
 * IP is attached server-side by the event collector.
 */
export function collectHandshakeMetadata(
  win: Window = window,
): HandshakeMetadata {
  const params = new URLSearchParams(win.location.search);
  const queryParams: Record<string, string> = {};
  params.forEach((value, key) => {
    queryParams[key] = value;
  });
  const userAgent = win.navigator.userAgent ?? "";

  return {
    userAgent,
    locale: params.get("locale") ?? win.navigator.language ?? "",
    referrer: win.document.referrer ?? "",
    queryParams,
    timestamp: new Date().toISOString(),
    isEmbedded: isEmbeddedInIframe(),
    isWebView: isWebViewUserAgent(userAgent),
  };
}
